import api from "services/api";

import { IElegibilityPlans, InactivePrice, PriceBand, PriceVersion } from "../../Setup.types";

export type { InactivePrice, PriceBand, PriceVersion };

export type VersionInput = {
  validFrom: string;
  kind: "SINGLE" | "AGE_BANDS";
  singleCents?: number;
  bands?: PriceBand[];
};

type Side = "active" | "inactive";

/** Faixas padrão RN 63, idênticas às do backend. Todas sem valor. */
export const RN63_BANDS: PriceBand[] = [
  [0, 18], [19, 23], [24, 28], [29, 33], [34, 38], [39, 43], [44, 48], [49, 53], [54, 58], [59, null],
].map(([fromAge, toAge]) => ({ fromAge: fromAge as number, toAge: toAge as number | null, cents: null }));

const base = (workspaceId: string, planCode: string) =>
  `/v2/workspaces/${workspaceId}/plans/${encodeURIComponent(planCode)}`;

export async function addPriceVersion(workspaceId: string, planCode: string, side: Side, input: VersionInput): Promise<IElegibilityPlans> {
  const { data } = await api.post(`${base(workspaceId, planCode)}/prices/${side}`, input);
  return data.data;
}

export async function correctPriceVersion(
  workspaceId: string,
  planCode: string,
  side: Side,
  validFrom: string,
  input: Omit<VersionInput, "validFrom">,
): Promise<IElegibilityPlans> {
  const { data } = await api.put(`${base(workspaceId, planCode)}/prices/${side}/${encodeURIComponent(validFrom)}`, input);
  return data.data;
}

export async function setInactiveMode(
  workspaceId: string,
  planCode: string,
  mode: { mode: "SAME_AS_ACTIVE" } | { mode: "OWN"; planName?: string; planCode?: string },
): Promise<IElegibilityPlans> {
  const { data } = await api.put(`${base(workspaceId, planCode)}/prices/inactive-mode`, mode);
  return data.data;
}

export async function setPlanContribution(workspaceId: string, planCode: string, value: boolean | null): Promise<IElegibilityPlans> {
  const { data } = await api.put(`${base(workspaceId, planCode)}/contribution`, { value });
  return data.data;
}

/** `null` (volta a valer o do contrato) vai como `contract` na query. */
export async function previewPlanContribution(
  workspaceId: string,
  planCode: string,
  value: boolean | null,
): Promise<{ wouldClose: number | null }> {
  const { data } = await api.get(`${base(workspaceId, planCode)}/contribution/preview`, {
    params: { value: value === null ? "contract" : String(value) },
  });
  return data.data;
}

/** Só para exibir "vigente" e "próximo reajuste": maior validFrom menor ou igual ao dia (YYYY-MM-DD). */
export function currentVersionOn(versions: PriceVersion[] | undefined, onDay: string): PriceVersion | null {
  let found: PriceVersion | null = null;
  for (const v of versions ?? []) {
    if (v.validFrom <= onDay && (!found || v.validFrom > found.validFrom)) found = v;
  }
  return found;
}

const LEGACY_VALUE = /^(R\$\s?)?\d{1,3}(\.\d{3})*,\d{2}$|^(R\$\s?)?\d+(,\d{2})?$/;

/** Mesma regra do backend: só valor inequívoco vira centavos; o resto não pré-preenche nada. */
export function legacyPrefillCents(planValue: unknown): number | null {
  if (typeof planValue !== "string") return null;
  const text = planValue.replace(/ /g, " ").trim();
  if (!LEGACY_VALUE.test(text)) return null;
  const [reais, centavos = "00"] = text.replace(/^R\$\s?/, "").replace(/\./g, "").split(",");
  return Number(reais) * 100 + Number(centavos);
}
